
import { useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { supabase, getEdgeFunctionUrl } from "@/integrations/supabase/client";

const NotFound = () => {
  const location = useLocation();
  const [diagnosticInfo, setDiagnosticInfo] = useState<string | null>(null);

  // Function to test Supabase edge functions
  const testEdgeFunctions = async () => {
    try {
      // Try invoking functions to test connectivity
      const webhookTest = await fetch('https://api.vitatee.com/functions/v1/stripe-webhook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test: true })
      });
      
      const webhookStatus = webhookTest.status;
      const webhookText = await webhookTest.text();
      
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!anon) {
        return `No anon key available. This might explain authentication issues.`;
      }
      
      // Test with auth
      const createTest = await fetch('https://api.vitatee.com/functions/v1/create-checkout', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anon}`,
          'apikey': anon
        },
        body: JSON.stringify({ test: true, email: 'test@example.com' })
      });
      
      const createStatus = createTest.status;
      const createText = await createTest.text();
      
      return `
Webhook Test: ${webhookStatus} - ${webhookText?.substring(0, 50)}...
Create Test: ${createStatus} - ${createText?.substring(0, 50)}...
Supabase URL: https://api.vitatee.com
      `;
    } catch (error: any) {
      return `Error testing edge functions: ${error.message}`;
    }
  };

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
    
    // Add more detailed logging to help debug redirects
    console.error("Full URL:", window.location.href);
    console.error("Search params:", location.search);
    console.error("Hash:", location.hash);
    console.error("State:", location.state);
    console.error("Key:", location.key);
    
    // Analyze search params
    const searchParams = new URLSearchParams(location.search);
    searchParams.forEach((value, key) => {
      console.log(`Search param: ${key} = ${value}`);
    });
    
    // Test Supabase edge functions and set diagnostic info
    testEdgeFunctions().then(setDiagnosticInfo);
  }, [location.pathname, location.search]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10 p-4">
        <div className="bg-card border rounded-lg p-8 shadow-sm max-w-md w-full text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-3">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2">404</h1>
          <p className="text-xl text-muted-foreground mb-6">Oops! Page not found</p>
          <p className="text-sm text-muted-foreground mb-6">
            The page you're looking for doesn't exist or has been moved.
            {location.search && (
              <span className="block mt-2">
                We noticed you came from a redirect. This might be due to an outdated link or a URL handling issue.
              </span>
            )}
          </p>
          <div className="flex flex-col space-y-2">
            <Button asChild className="w-full">
              <Link to="/">Return to Home</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/auth">Go to Sign In</Link>
            </Button>
            
            {diagnosticInfo && (
              <div className="mt-6 text-left text-xs text-muted-foreground bg-muted p-2 rounded overflow-auto max-h-40">
                <p className="font-semibold">Diagnostic Info:</p>
                <pre className="whitespace-pre-wrap break-all">{diagnosticInfo}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default NotFound;
